import { atom } from 'jotai';
import { FormProgress, FormStep } from '../types/form';

// Load initial state from localStorage if available
const loadInitialState = (): FormProgress => {
  try {
    const savedState = localStorage.getItem('formProgress');
    if (savedState) {
      const parsed = JSON.parse(savedState);
      // Convert date strings back to Date objects
      if (parsed.data.financialTerms?.installments) {
        parsed.data.financialTerms.installments = parsed.data.financialTerms.installments.map(
          (inst: any) => ({
            ...inst,
            dueDate: new Date(inst.dueDate)
          })
        );
      }
      return parsed;
    }
  } catch (error) {
    console.error('Error loading form state:', error);
  }
  return {
    currentStep: 'customer-info',
    completedSteps: [],
    isLocked: false,
    data: {}
  };
};

const baseAtom = atom<FormProgress>(loadInitialState());

export const formProgressAtom = atom(
  (get) => get(baseAtom),
  (get, set, update: FormProgress | ((prev: FormProgress) => FormProgress)) => {
    const newValue = typeof update === 'function' ? update(get(baseAtom)) : update;

    // If data is explicitly empty or has specific reset indicators, don't merge
    const isReset = Object.keys(newValue.data).length === 0 ||
                    (newValue.data.powerOfAttorney === null && Object.keys(newValue.data).length === 1);

    const currentData = get(baseAtom).data;
    const finalData = isReset ? newValue.data : {
      ...currentData,
      ...newValue.data
    };

    const finalValue = {
      ...newValue,
      data: finalData
    };

    set(baseAtom, finalValue);
    try {
      localStorage.setItem('formProgress', JSON.stringify(finalValue));
    } catch (error) {
      console.error('Error saving form state:', error);
    }
  }
);

export const formSteps: { id: FormStep; label: string; icon: keyof typeof import('lucide-react') }[] = [
  { id: 'customer-info', label: 'Informações do Cliente', icon: 'User' },
  { id: 'installation-location', label: 'Local da Instalação', icon: 'MapPin' },
  { id: 'technical-config', label: 'Configuração Técnica', icon: 'Zap' },
  { id: 'financial-terms', label: 'Termos Financeiros', icon: 'DollarSign' },
  { id: 'review', label: 'Revisão e Geração', icon: 'FileText' }
];

// ✅ Função auxiliar para obter o passo anterior com base no passo atual
export const getPreviousStep = (currentStep: FormStep): FormStep | null => {
  const currentIndex = formSteps.findIndex(step => step.id === currentStep);
  return currentIndex > 0 ? formSteps[currentIndex - 1].id : null;
};
